import { z } from "zod";

// POST /v1/notifications rejects a scheduled_at further ahead than this.
const MAX_SCHEDULE_DAYS = 30;

export const createNotificationSchema = z
  .object({
    title: z
      .string()
      .min(1, "Title is required")
      .max(50, "Title must be 50 characters or less"),
    message: z
      .string()
      .min(1, "Message is required")
      .max(200, "Message must be 200 characters or less"),
    link: z
      .string()
      .url("Must be a valid URL")
      .optional()
      .or(z.literal("")),
    image: z
      .instanceof(File)
      .optional()
      .nullable(),
    scheduleOption: z.enum(["now", "in_one_hour", "custom"]),
    customDateTime: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.scheduleOption === "custom") {
        return !!data.customDateTime && data.customDateTime.trim() !== "";
      }
      return true;
    },
    {
      message: "Date and time is required when custom schedule is selected",
      path: ["customDateTime"],
    }
  )
  .refine(
    (data) => {
      if (data.scheduleOption === "custom" && data.customDateTime) {
        const selectedDate = new Date(data.customDateTime);
        const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
        return selectedDate >= oneHourFromNow;
      }
      return true;
    },
    {
      message: "Selected date and time must be at least 1 hour from now",
      path: ["customDateTime"],
    }
  )
  .refine(
    (data) => {
      if (data.scheduleOption === "custom" && data.customDateTime) {
        const selectedDate = new Date(data.customDateTime);
        const maxDate = new Date(Date.now() + MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000);
        return selectedDate <= maxDate;
      }
      return true;
    },
    {
      message: `Selected date and time must be within ${MAX_SCHEDULE_DAYS} days`,
      path: ["customDateTime"],
    }
  );

export type CreateNotificationFormData = z.infer<
  typeof createNotificationSchema
>;

export const onboardingNotificationSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(50, "Title must be 50 characters or less"),
  message: z
    .string()
    .min(1, "Message is required")
    .max(200, "Message must be 200 characters or less"),
});

export type OnboardingNotificationFormData = z.infer<
  typeof onboardingNotificationSchema
>;
